// filepath: apps/web/src/operator/OperatorShell.tsx
//
// FR-UI-OPS-2 AC-1 / AC-7 / AC-8 / FR-UI-OPS-3 — persistent operator
// nav shell.
//
// Layout route element rendered as the parent of every section
// route in `OperatorRoutes`. Owns:
//   - the persistent left-nav rail (chrome mirrors the design-system
//     `SidebarNav` inventory primitive: `var(--surface-sidebar)`
//     background, `border-right`, vertical stack of items);
//   - the `<main id="operator-root">` landmark + visually-hidden
//     `<h1>Operator Console</h1>` (FR-UI-CORE-9), preserved from the
//     pre-router `OperatorRoot`;
//   - the `<Outlet/>` where the matched section route renders.
//
// The shell is rendered INSIDE `<OperatorTokenGate>` (AC-7 — the token
// prompt precedes any protected section) and INSIDE `<BrowserRouter>`
// (AC-8 — section nav uses react-router-dom). It mounts only when
// `OperatorRoot` mounts, which only happens on the platform apex
// behind the `isOperatorSurface` gate in `App.tsx`.
//
// SECURITY (INV-9): all nav labels are static platform chrome sourced
// from the i18n bundle — no tenant/authored text, no
// `dangerouslySetInnerHTML`. `<NavLink>` renders plain `<a>` elements.
//
// FR-UI-OPS-3 note: nav items remain `<NavLink>` (not the design-system
// `SidebarNav` `onSelect(id)` callback) because react-router's
// `aria-current="page"` semantics are required by the existing
// `operatorShell.test.tsx` assertions. The visual chrome (background,
// spacing, typography) follows the `SidebarNav` inventory primitive so
// the operator surface is visually aligned with the design system
// without losing the router-aware active-state semantics.

import React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "@/i18n/useTranslation";
import { LOCALES } from "@/i18n/locales";
import { useLocalePreference } from "@/features/cabinet/chrome/useLocalePreference";
import i18n from "@/i18n/i18n";

interface NavItem {
  /** Section-relative destination (resolved under the router basename). */
  to: string;
  /** i18n key for the visible label. */
  labelKey: string;
  /** English fallback for the label (used only when the key is absent). */
  fallback: string;
  /** `end` matches the Summary index route exactly (no nested match). */
  end: boolean;
  /** Stable hook for TestDesigner / axe-core assertions. */
  testId: string;
}

/**
 * Exactly four nav sections (AC-1). Order is fixed; the array is
 * frozen so a future editor cannot accidentally add a fifth.
 */
const NAV_ITEMS: ReadonlyArray<NavItem> = [
  {
    to: "/",
    labelKey: "operator.nav.summary",
    fallback: "Summary",
    end: true,
    testId: "operator-nav-summary",
  },
  {
    to: "/tenants",
    labelKey: "operator.nav.tenants",
    fallback: "Tenants",
    end: false,
    testId: "operator-nav-tenants",
  },
  {
    to: "/effects",
    labelKey: "operator.nav.effects",
    fallback: "Effects / Ops",
    end: false,
    testId: "operator-nav-effects",
  },
  {
    to: "/audit",
    labelKey: "operator.nav.audit",
    fallback: "Audit",
    end: false,
    testId: "operator-nav-audit",
  },
];

/**
 * OperatorShell — layout route element. Renders the persistent
 * left-nav rail and the `<main>` landmark; the matched section route
 * renders inside `<Outlet/>`.
 *
 * `<NavLink>` renders a native `<a>` (keyboard-focusable, Enter to
 * activate) and sets `aria-current="page"` on the active link —
 * satisfying the keyboard-navigable + axe-core-clean requirements
 * of AC-1 / FR-UI-CORE-9 without extra ARIA wiring.
 *
 * Visual chrome follows the design-system `SidebarNav` inventory
 * primitive: `var(--surface-sidebar)` background, `border-right: 1px
 * solid var(--border-default)`, items stacked vertically with the
 * active state highlighted via `var(--color-primary)` text +
 * `var(--surface-active)` background (token-only, no raw hex).
 */
export function OperatorShell(): React.ReactElement {
  const { t } = useTranslation();
  const { setLocale } = useLocalePreference();

  // FR-UI-NFR-4 note: the operator surface retains its bespoke chrome
  // (own topbar + left nav rail). The shared `AppChrome` primitive is
  // NOT used here because AppChrome pulls tenant / env-badge data
  // from `TenantProvider` and the operator surface is mounted above
  // the tenant boundary (operator apex). Routing the operator
  // through AppChrome would require wrapping the test in
  // `TenantProvider` + `QueryClientProvider`, breaking the AC-1
  // layout-route invariants asserted by `operatorShell.test.tsx`.
  // The operator's bespoke topbar + sidebar visually mirror the
  // AppChrome tokens (see `apps/web/src/chrome/AppChrome.tsx` for the
  // design pin at commit 9fd1129) so the chrome remains visually
  // consistent across all four variants.
  return (
    <div
      className="operator-shell"
      data-testid="operator-shell"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        background: "var(--surface-page)",
        fontFamily: "system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
        color: "var(--text-primary)",
      }}
    >
      <header
        data-testid="operator-topbar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          height: "var(--space-12, 48px)",
          padding: "0 var(--space-4)",
          background: "var(--surface-sidebar)",
          borderBottom: "1px solid var(--border-default)",
          flex: "0 0 auto",
        }}
      >
        <label
          htmlFor="operator-locale-select"
          style={{
            fontSize: "var(--text-xs)",
            color: "var(--text-subtle)",
            marginRight: "var(--space-2)",
          }}
        >
          {t("operator.topbar.localeLabel", "Language")}
        </label>
        <select
          id="operator-locale-select"
          data-testid="operator-locale-select"
          value={i18n.language}
          onChange={(e) => {
            const next = e.target.value;
            if (LOCALES.includes(next as (typeof LOCALES)[number])) {
              setLocale(next as (typeof LOCALES)[number]);
            }
          }}
          style={{
            fontSize: "var(--text-sm)",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-page)",
            color: "var(--text-primary)",
            padding: "2px var(--space-2)",
            cursor: "pointer",
          }}
        >
          {LOCALES.map((loc) => (
            <option key={loc} value={loc}>
              {t(`cabinet.languageMenu.label.${loc}`, loc)}
            </option>
          ))}
        </select>
      </header>
      <div style={{ display: "flex", flex: "1 1 auto", minHeight: 0 }}>
        <aside
          className="operator-nav"
          style={{
            width: "220px",
            minHeight: "100vh",
            background: "var(--surface-sidebar)",
            borderRight: "1px solid var(--border-default)",
            padding: "var(--space-4) 0",
            display: "flex",
            flexDirection: "column",
            gap: "var(--space-6)",
            boxSizing: "border-box",
            flex: "0 0 auto",
          }}
        >
          <nav
            aria-label={t("operator.nav.label", "Operator sections")}
            data-testid="operator-nav"
          >
            <ul
              className="operator-nav-list"
              style={{
                listStyle: "none",
                margin: 0,
                padding: 0,
              }}
            >
              {NAV_ITEMS.map((item) => (
                <li
                  key={item.to}
                  className="operator-nav-item"
                  style={{ margin: 0, padding: 0 }}
                >
                  <NavLink
                    to={item.to}
                    end={item.end}
                    data-testid={item.testId}
                    className="operator-nav-link"
                    style={({ isActive }) => ({
                      display: "block",
                      padding: "8px 16px",
                      fontSize: "var(--text-sm)",
                      textDecoration: "none",
                      cursor: "pointer",
                      fontWeight: isActive
                        ? "var(--font-semibold)"
                        : "var(--font-medium)",
                      color: isActive
                        ? "var(--color-primary)"
                        : "var(--text-body)",
                      background: isActive
                        ? "var(--surface-active)"
                        : "transparent",
                      borderRadius: "0 var(--radius-sm) var(--radius-sm) 0",
                    })}
                  >
                    {t(item.labelKey, item.fallback)}
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
        <main
          id="operator-root"
          className="operator-main"
          style={{
            flex: "1 1 auto",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            background: "var(--surface-page)",
          }}
        >
          <div
            className="operator-root"
            style={{
              padding: "var(--space-8)",
              maxWidth: 1280,
              margin: "0 auto",
              width: "100%",
              boxSizing: "border-box",
            }}
          >
            <h1
              id="operator-root-title"
              className="visually-hidden"
              style={{
                position: "absolute",
                width: 1,
                height: 1,
                padding: 0,
                margin: -1,
                overflow: "hidden",
                clip: "rect(0,0,0,0)",
                whiteSpace: "nowrap",
                border: 0,
              }}
            >
              {t("operator.title", "Operator Console")}
            </h1>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
